import { useRouter } from 'next/router'
import React from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { generatePretendUserUrl } from 'shared/hooks/use-user/utils/generate-pretend-user-url'
import { UserInterface } from 'shared/types/user-interface'
import { hasSubAccounts } from '../utils/has-subaccounts'

interface UseUserSubaccountsProps {
  dashboardUser?: UserInterface
  isSupportImpersonation: boolean
}

export const useUserSubaccounts = ({
  dashboardUser,
  isSupportImpersonation,
}: UseUserSubaccountsProps) => {
  const { push } = useRouter()
  const { t } = useLocoTranslation()

  const subAccountsData = () => {
    if (!dashboardUser || !hasSubAccounts(dashboardUser) || !dashboardUser.mainUser)
      return undefined

    const getCustomCaption = (image: string | undefined, name: string) => {
      return (
        <div className={`flex items-center gap-3 text-start`}>
          {image && (
            <img
              className="h-7 w-7 overflow-hidden rounded-full object-cover"
              src={image}
              alt={name}
            />
          )}
          <div className={'flex flex-col'}>{name}</div>
        </div>
      )
    }

    return [
      {
        id: dashboardUser.mainUser.user.id,
        caption: dashboardUser.mainUser.displayName,
        customCaption: getCustomCaption(
          dashboardUser?.subaccountsAvatarUrls[dashboardUser.mainUser.user.id] ?? undefined,
          dashboardUser.mainUser.displayName,
        ),
      },
      ...dashboardUser.subaccounts.map(({ user: { id }, displayName, profileImage }) => ({
        id,
        caption: displayName,
        customCaption: getCustomCaption(
          dashboardUser?.subaccountsAvatarUrls[id] ?? undefined,
          displayName,
        ),
      })),
    ]
  }

  const changeSubAccount = (id?: number) => {
    if (!id || id === dashboardUser?.id || !dashboardUser?.mainUser) return

    const isImpersonateToMainAccount = id === dashboardUser?.mainUser.user.id

    let query = encodeURIComponent(id)

    if (isImpersonateToMainAccount && !isSupportImpersonation) query = '_exit'

    push(generatePretendUserUrl(query))
  }

  return {
    subAccountsData,
    changeSubAccount,
  }
}
